import { useCallback, useEffect, useState } from "react";
import { db } from "../../../Firebase";
import useClickedOutside from "../../../hooks/useClickedOutside";
import HistoryModal from "../../HistoryModal/HistoryModal";

const History = ({ id, snapKey, user, storyEngineId, onRestore, title, close }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  const ref = useClickedOutside(() => close());

  useEffect(() => {
    setSelectedSnapshot(null);
  }, [id, storyEngineId]);

  // Paginated fetch function for story engine snapshots
  const fetchHistoryPaginated = useCallback(
    async (limit = 50, offset = 0) => {
      if (!storyEngineId || !user || !snapKey) return [];

      try {
        let query = db
          .collection("storyEnginePropSnapshots")
          .where("key", "==", snapKey)
          .where("storyEngineId", "==", storyEngineId)
          .where("userId", "==", user.uid)
          .orderBy("createdAt", "desc");

        if (id) {
          query = query.where("id", "==", id);
        }

        // For offset-based pagination, we need to efficiently skip documents
        if (offset > 0) {
          // Use a larger limit to get the cursor document more efficiently
          const cursorLimit = Math.min(offset + 1, 1000); // Cap at 1000 to avoid memory issues
          const cursorQuery = query.limit(cursorLimit);
          const cursorDocs = await cursorQuery.get();

          if (cursorDocs.docs.length > offset) {
            // We have enough documents to establish a cursor
            const cursorDoc = cursorDocs.docs[offset];
            query = query.startAfter(cursorDoc).limit(limit);
          } else {
            // Not enough documents to paginate further
            return [];
          }
        } else {
          query = query.limit(limit);
        }

        const snapshotDocs = await query.get();

        const docs = snapshotDocs.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt.toDate(),
          data: { displayName: doc.data().contents.substring(0, 20) },
        }));

        return docs;
      } catch (error) {
        return [];
      }
    },
    [storyEngineId, user, snapKey, id]
  );

  // Legacy fetch for backward compatibility
  useEffect(() => {
    const fetchSnapshots = async () => {
      let query = db
        .collection("storyEnginePropSnapshots")
        .where("key", "==", snapKey) //
        .where("storyEngineId", "==", storyEngineId)
        .where("userId", "==", user.uid)
        .orderBy("createdAt", "desc");

      if (id) {
        query = query.where("id", "==", id);
      }

      const snapshotDocs = await query.get();

      const docs = snapshotDocs.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt.toDate(),
        data: { displayName: doc.data().contents.substring(0, 20) },
      }));

      setSnapshots(docs);
      setSelectedSnapshot(docs[0]);
    };

    fetchSnapshots();
  }, []);

  const handleClick = (snapshot) => {
    setSelectedSnapshot(snapshot);
  };

  const handleRestore = () => {
    if (selectedSnapshot) {
      onRestore(selectedSnapshot.contents);
      close();
    }
  };

  return (
    <HistoryModal
      isOpen={true}
      onClose={close}
      selectedItem={
        <div
          tabIndex={0}
          className="main-content p-4"
        >
          {selectedSnapshot && (
            <pre
              className="block whitespace-pre-wrap break-words text-md text-[var(--grey-0)] overflow-x-auto pr-4"
              tabIndex={0}
            >
              {selectedSnapshot.contents}
            </pre>
          )}
        </div>
      }
      historyItems={snapshots}
      onSelectItem={handleClick}
      onRestoreItem={handleRestore}
      selectedItemId={selectedSnapshot?.id}
      title={`${title} History`}
      fetchHistory={fetchHistoryPaginated}
      usePagination={true}
    />
  );
};

export default History;
